import { useEffect } from 'react'
import {
  Box, Flex, HStack, VStack, Text, IconButton, Drawer, Portal, CloseButton, Menu, useDisclosure,
} from '@chakra-ui/react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { FiMenu, FiUser, FiLogOut, FiUsers, FiMail, FiGrid, FiSettings, FiKey } from 'react-icons/fi'
import { useAuth } from '../../../hooks/useAuth'
import { useProfile } from '../../../hooks/useProfile'
import { signOutOrganizer } from '../../../lib/session/organizerSession'
import { UserAvatar } from '../../admin/UserAvatar'
import { ProfileEditModal } from '../../profile/ProfileEditModal'
import { CommitteeSwitcher } from '../CommitteeSwitcher'
import { ModuleNav } from '../ModuleNav'
import { MainContentRegion } from '../MainContentRegion'
import type { SidebarContextType } from '../DashboardLayout'

export function TabletDashboardShell() {
  const location = useLocation()
  const navigate = useNavigate()
  const { isAdmin } = useAuth()
  const { data: profile } = useProfile()
  const nav = useDisclosure()
  const profileModal = useDisclosure()

  // Drawer-closed default; close it whenever the route changes (tap-to-navigate then dismiss).
  useEffect(() => {
    nav.onClose()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  const handleLogout = () => void signOutOrganizer()

  // Tablet has no collapsible rail; provide a stub so pages calling useSidebarContext() work.
  const stubSidebar: SidebarContextType = { collapsed: false, setCollapsed: () => {} }

  return (
    <Flex direction="column" minH="100vh" bg="canvas">
      {/* Top bar */}
      <Box bg="surface" borderBottom="1px" borderColor="border" px={4} py={3} position="sticky" top={0} zIndex={10}>
        <HStack justify="space-between">
          <HStack gap={2}>
            <IconButton
              aria-label="Open navigation"
              variant="ghost"
              colorPalette="gray"
              onClick={nav.onOpen}
              _touch={{ minH: '2.75rem', minW: '2.75rem' }}
            >
              <FiMenu />
            </IconButton>
            <Box w={7} h={7} bg="accent" borderRadius="md" display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
              <Text color="accent.fg" fontWeight="bold" fontSize="sm">BT</Text>
            </Box>
            <Text fontWeight="bold" fontSize="md">ORCL</Text>
          </HStack>

          <Menu.Root positioning={{ placement: 'bottom-end' }}>
            <Menu.Trigger minW="44px" minH="44px" display="flex" alignItems="center" justifyContent="center">
              <UserAvatar userId={profile?.id ?? ''} firstName={profile?.firstName ?? ''} portraitUrl={profile?.portraitUrl} size="sm" />
            </Menu.Trigger>
            <Portal><Menu.Positioner><Menu.Content>
              <Menu.Item onSelect={profileModal.onOpen} value="profile"><FiUser />Profile</Menu.Item>
              {isAdmin && (
                <>
                  <Menu.Separator />
                  <Menu.Item onSelect={() => navigate('/dashboard/admin/users')} value="users"><FiUsers />Users</Menu.Item>
                  <Menu.Item onSelect={() => navigate('/dashboard/admin/grants')} value="grants"><FiKey />Access Grants</Menu.Item>
                  <Menu.Item onSelect={() => navigate('/dashboard/admin/invites')} value="invites"><FiMail />Invites</Menu.Item>
                  <Menu.Item onSelect={() => navigate('/dashboard/admin/committees')} value="committees"><FiGrid />Committees</Menu.Item>
                </>
              )}
              <Menu.Separator />
              <Menu.Item onSelect={() => navigate('/dashboard/settings')} value="settings"><FiSettings />Settings</Menu.Item>
              <Menu.Separator />
              <Menu.Item onSelect={handleLogout} value="logout"><FiLogOut />Logout</Menu.Item>
            </Menu.Content></Menu.Positioner></Portal>
          </Menu.Root>
        </HStack>

        {/* WL-6: the "Viewing:" committee switcher lives in the nav chrome under the ORCL banner
            (self-hides when the caller has no committees to switch to). */}
        <CommitteeSwitcher />
      </Box>

      {/* Nav drawer (left overlay, labelled ModuleNav — never an icon rail) */}
      <Drawer.Root placement="start" open={nav.open} onOpenChange={(e) => { if (!e.open) nav.onClose() }}>
        <Portal>
          <Drawer.Backdrop />
          <Drawer.Positioner>
            <Drawer.Content>
              <Drawer.CloseTrigger asChild><CloseButton size="sm" _touch={{ minH: '2.75rem', minW: '2.75rem' }} /></Drawer.CloseTrigger>
              <Drawer.Header>
                <HStack gap={2}>
                  <Box w={7} h={7} bg="accent" borderRadius="md" display="flex" alignItems="center" justifyContent="center">
                    <Text color="accent.fg" fontWeight="bold" fontSize="sm">BT</Text>
                  </Box>
                  <Text fontWeight="bold">ORCL</Text>
                </HStack>
              </Drawer.Header>
              <Drawer.Body px={2}>
                <VStack align="stretch" gap={0}>
                  <ModuleNav pathname={location.pathname} collapsed={false} isAdmin={isAdmin} />
                </VStack>
              </Drawer.Body>
            </Drawer.Content>
          </Drawer.Positioner>
        </Portal>
      </Drawer.Root>

      {/* Content — full width beneath the top bar */}
      <Box flex={1} overflowY="auto">
        <MainContentRegion pathname={location.pathname}>
          <Outlet context={stubSidebar} />
        </MainContentRegion>
      </Box>

      <ProfileEditModal isOpen={profileModal.open} onClose={profileModal.onClose} />
    </Flex>
  )
}
